import axios, { AxiosResponse } from 'axios';
import { API_BASE_URI } from 'library/constants/api';
import { isEmpty } from 'lodash';
import moment from 'moment';

// Base URLs
const API_BASE_URL = `${API_BASE_URI}/property`;
export const ADMIN_BASE_URL = `${API_BASE_URI}/admin`;

const DELETED_IMAGE_URLS_KEY = (propertyId: string | number) =>
  `deletedImageUrls_${String(propertyId)}`;

/** Persist deleted image URLs for this property so we can filter stale images/list responses */
export const setDeletedImageUrlsForProperty = (
  propertyId: string | number,
  deletedUrls: string[],
): void => {
  if (typeof sessionStorage === 'undefined' || !deletedUrls?.length) return;
  try {
    const key = DELETED_IMAGE_URLS_KEY(propertyId);
    const existing = sessionStorage.getItem(key);
    const existingSet: string[] = existing ? JSON.parse(existing) : [];
    const merged = Array.from(new Set(existingSet.concat(deletedUrls)));
    sessionStorage.setItem(key, JSON.stringify(merged));
  } catch (_) {
    // ignore
  }
};

/** Remove deleted URLs from imageUrlList (workaround when backend list API returns stale data) */
const filterOutDeletedUrls = (
  imageUrlList: Record<string, string[]> | undefined,
  deletedUrls: string[],
): Record<string, string[]> => {
  if (!imageUrlList || !deletedUrls?.length) return imageUrlList || {};
  const set = new Set(deletedUrls);
  const out: Record<string, string[]> = {};
  Object.keys(imageUrlList).forEach((category) => {
    const kept = (imageUrlList[category] || []).filter((url) => !set.has(url));
    if (kept.length > 0) out[category] = kept;
  });
  return out;
};

const normalizeImageUrlForCompare = (url: string): string => {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch (_) {
    // Fallback for non-standard URLs
    return url.split('?')[0].split('#')[0];
  }
};

/** Normalize imageUrlList by removing duplicate URLs globally */
const dedupeImageUrlList = (
  imageUrlList: Record<string, string[]> | undefined,
): Record<string, string[]> => {
  if (!imageUrlList) return {};
  const out: Record<string, string[]> = {};
  const globallySeen = new Set<string>();
  Object.keys(imageUrlList).forEach((category) => {
    const urls = imageUrlList[category] || [];
    const categorySeen = new Set<string>();
    const unique = urls.filter((url) => {
      const normalized = normalizeImageUrlForCompare(url);
      if (!normalized) return false;
      if (categorySeen.has(normalized)) return false;
      if (globallySeen.has(normalized)) return false;
      categorySeen.add(normalized);
      globallySeen.add(normalized);
      return true;
    });
    if (unique.length > 0) out[category] = unique;
  });
  return out;
};

/**
 * Video (and some multipart) endpoints may return HTTP 2xx with an empty body.
 * `response?.data` is then falsy; without this we incorrectly return a failure envelope.
 */
const normalizeMultipartSuccessBody = (
  response: AxiosResponse | null | undefined,
): any => {
  if (!response) {
    return { success: false, message: 'No data', result: [] };
  }
  const { status, data } = response;
  if (status >= 200 && status < 300) {
    if (data === '' || data === null || data === undefined) {
      return {
        responseInformation: { responseCode: 0, responseMessage: 'OK' },
        result: true,
      };
    }
    if (typeof data === 'string' && data.trim() === '') {
      return {
        responseInformation: { responseCode: 0, responseMessage: 'OK' },
        result: true,
      };
    }
  }
  return data ?? { success: false, message: 'No data', result: [] };
};

const appendVideoFile = (formData: FormData, fieldName: string, file: unknown): void => {
  if (file instanceof File) {
    formData.append(fieldName, file, file.name);
  } else if (file instanceof Blob) {
    formData.append(fieldName, file, 'video.mp4');
  } else if (file != null) {
    formData.append(fieldName, file as any);
  }
};

// Type definitions
interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  result: T;
  data?: T;
  responseInformation?: {
    isSuccess: boolean;
    responseMessage: string;
  };
}

interface PropertyAmenity {
  id: number;
  name: string;
  icon: string;
  category?: string;
}

interface PropertyHighlight {
  id: number;
  name: string;
  description?: string;
}

interface PropertyAttraction {
  id: number;
  name: string;
  distance?: number;
  type?: string;
}

interface PropertyType {
  id: number;
  name: string;
  icon: string;
  subOptions?: PropertyType[];
}

interface PropertyOffering {
  id: number;
  name: string;
  icon?: string;
  parentId?: number;
}

interface PropertyDetails {
  id: number;
  title: string;
  description: string;
  location: any;
  amenities: PropertyAmenity[];
  type: PropertyType;
  subType: PropertyType;
  offerings: PropertyOffering[];
}

export interface PropertyTile {
  propertyId: number;
  title: string;
  state: string;
  city: string;
  status: string;
  onboardingStage: number;
  imageUrl: string | null;
  hostId: number | null;
  hostName: string | null;
  hostContactNumber: string | null;
  hostType: string | null;
  createdAt: string;
  bhkType: number | null;
  hostPro: boolean;
  businessType: 'B2B' | 'B2C' | null;
}

interface PropertyPhoto {
  id: number;
  url: string;
  category: string;
  isThumbnail: boolean;
}

interface PropertyReview {
  id: number;
  userId: number;
  rating: number;
  comment: string;
  createdAt: string;
  userName?: string;
}

// Axios v1 expects a flexible header map type
type ApiHeaders = Record<string, string>;

// Standard headers helper
export const getStandardHeaders = (): ApiHeaders => ({
  Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
  'Content-Type': 'application/json',
  'Access-Control-Allow-Origin': '*',
});

// Error handler helper
export const handleApiError = (error: any): void => {
  if (error?.response?.status === 403) {
    localStorage.clear();
    sessionStorage.clear();
    window.open('/sign-in', '_self');
  }
};

const PropertyService = {
  // Function to get data from the API for amenities
  getAmenities: async (): Promise<ApiResponse<PropertyAmenity[]>> => {
    try {
      const response = await axios
        .get(`${API_BASE_URL}/getPropertyAmenitiesList`)
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error fetching data:', error);
      throw error;
    }
  },

  getPropertyHighlightsList: async (): Promise<
    ApiResponse<PropertyHighlight[]>
  > => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .get(`${API_BASE_URL}/getPropertyHighlightsList`, { headers })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error fetching data:', error);
      throw error;
    }
  },

  getPropertyAttractionsList: async (): Promise<
    ApiResponse<PropertyAttraction[]>
  > => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .get(`${API_BASE_URL}/getPropertyAttractionsList`, { headers })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error fetching data:', error);
      throw error;
    }
  },

  getBysVerificationCategoryList: async () => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .get(`${API_BASE_URL}/getBysVerificationCategoryList`, { headers })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error fetching data:', error);
      throw error;
    }
  },

  savePropertyHighlights: async (data: {
    propertyId: number;
    highlights: number[];
  }): Promise<ApiResponse> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .post(`${API_BASE_URL}/savePropertyHighlights`, data, {
          headers,
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error posting data:', error);
      throw error;
    }
  },

  updatePropertyHighlights: async (data: {
    propertyId: number;
    highlights: number[];
  }): Promise<ApiResponse> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .post(`${API_BASE_URL}/updatePropertyHighlights`, data, {
          headers,
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error posting data:', error);
      throw error;
    }
  },

  uploadBrochure: async (data) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const formData = new FormData();
      formData.append(`propertyId`, data.propertyId);
      formData.append('brochure', data.brochure);

      const response = await axios
        .post(`${API_BASE_URL}/brochure/upload`, formData, {
          headers,
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error posting data:', error);
      throw error;
    }
  },

  updateBrochure: async (data) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const formData = new FormData();
      formData.append(`propertyId`, data.propertyId);
      formData.append('brochure', data.brochure);
      formData.append('deletedBrochureUrl', data.deletedBrochureUrl);

      const response = await axios
        .post(`${API_BASE_URL}/brochure/update`, formData, {
          headers,
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error posting data:', error);
      throw error;
    }
  },

  videoUpload: async (data) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const formData = new FormData();
      formData.append(`propertyId`, String(data.propertyId));
      appendVideoFile(formData, 'video', data.video);
      formData.append('onboardingStage', String(data.onboardingStage));
      formData.append('videoType', data.videoType || 'main');

      const response = await axios
        .post(`${API_BASE_URL}/videos/upload`, formData, {
          headers,
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return normalizeMultipartSuccessBody(response);
    } catch (error) {
      // Handle error
      console.error('Error posting data:', error);
      throw error;
    }
  },

  videoUpdate: async (data) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const formData = new FormData();
      formData.append(`propertyId`, String(data.propertyId));
      // Backend types `addVideo` as a MultipartFile — sending it as a plain string field
      // (e.g. '') for delete-only requests fails Spring's type binding with a 400 before the
      // controller even runs, so the key must be omitted entirely when there's no file.
      if (data.addVideo instanceof File || data.addVideo instanceof Blob) {
        const blob = data.addVideo as Blob;
        const name = blob instanceof File ? blob.name : 'video.mp4';
        formData.append('addVideo', blob, name);
      }
      // Field name must match UpdatePropertyVideosForm#deletedVideoURL exactly (Spring
      // ModelAttribute binding is case-sensitive) — same convention as images/update's
      // `deleteImagesURL`.
      formData.append('deletedVideoURL', data.deletedVideoUrl ?? '');
      formData.append('videoType', data.videoType || 'main');

      const response = await axios
        .post(`${API_BASE_URL}/videos/update`, formData, {
          headers,
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return normalizeMultipartSuccessBody(response);
    } catch (error) {
      // Handle error
      console.error('Error posting data:', error);
      throw error;
    }
  },

  // videos/list
  getPropertyVideo: async (propertyId) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const cacheBust = `_t=${Date.now()}`;
      const response = await axios
        .get(`${API_BASE_URL}/videos/list?propertyId=${propertyId}&${cacheBust}`, {
          headers,
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error fetching data:', error);
      throw error;
    }
  },

  savePropertyAttractionsDistanceMapping: async (data) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .post(`${API_BASE_URL}/savePropertyAttractionsDistanceMapping`, data, {
          headers,
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error posting data:', error);
      throw error;
    }
  },

  savePropertyVerificationCategoryMapping: async (data) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .post(`${API_BASE_URL}/savePropertyVerificationCategoryMapping`, data, {
          headers,
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error posting data:', error);
      throw error;
    }
  },

  addPropertyTagMapping: async (data) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .post(`${API_BASE_URL}/addPropertyTagMapping`, data, {
          headers,
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error posting data:', error);
      throw error;
    }
  },

  updatePropertyVerificationCategoryMapping: async (data) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .post(
          `${API_BASE_URL}/updatePropertyVerificationCategoryMapping`,
          data,
          {
            headers,
          },
        )
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error posting data:', error);
      throw error;
    }
  },

  updatePropertyAttractionsDistanceMapping: async (data) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .post(
          `${API_BASE_URL}/updatePropertyAttractionsDistanceMapping`,
          data,
          {
            headers,
          },
        )
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error posting data:', error);
      throw error;
    }
  },

  getPropertyOfferingsList: async (): Promise<
    ApiResponse<PropertyOffering[]>
  > => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .get(`${API_BASE_URL}/getPropertyOfferingsList`, { headers })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error fetching data:', error);
      throw error;
    }
  },

  getPropertyDetailsFieldList: async () => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .get(`${API_BASE_URL}/getPropertyDetailsFieldList`, { headers })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error fetching data:', error);
      throw error;
    }
  },

  getPropertyTypeList: async (): Promise<ApiResponse<PropertyType[]>> => {
    // const headers = {
    //   Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
    //   'Content-Type': 'application/json',
    //   'Access-Control-Allow-Origin': '*',
    // };

    try {
      const response = await axios
        .get(`${API_BASE_URL}/getPropertyTypeList`)
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error fetching data:', error);
      throw error;
    }
  },
  // getAdditionalPriceOptionsList
  getAdditionalPriceOptionsList: async () => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .get(`${API_BASE_URL}/getAdditionalPriceOptionsList`, { headers })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error fetching data:', error);
      throw error;
    }
  },

  getPropertyDetailsById: async (
    propertyId: number | string,
  ): Promise<ApiResponse<PropertyDetails>> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .get(
          `${API_BASE_URL}/getPropertyDetailsById?propertyId=${propertyId}&_t=${Date.now()}`,
          { headers },
        )
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error fetching data:', error);
      throw error;
    }
  },

  getPropertyTilesByIds: async (
    propertyIds: Array<number | string>,
  ): Promise<ApiResponse<PropertyTile[]>> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .get(`${API_BASE_URL}/tiles`, {
          headers,
          params: { propertyIds: propertyIds.join(',') },
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      console.error('Error fetching property tiles:', error);
      throw error;
    }
  },

  getPhotos: async (propertyId) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    // Cache-bust so browser doesn't return stale list after image updates
    const cacheBust = `_t=${Date.now()}`;
    try {
      const response = await axios
        .get(`${API_BASE_URL}/images/list?propertyId=${propertyId}&${cacheBust}`, {
          headers,
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      const data = response?.data || {
        responseInformation: { responseCode: -1 },
        result: {},
      };
      // When backend list API still returns deleted URLs, filter them out using stored delete list
      const propertyIdStr = propertyId != null ? String(propertyId) : '';
      if (
        propertyIdStr &&
        data?.result?.imageUrlList &&
        typeof sessionStorage !== 'undefined'
      ) {
        try {
          const key = DELETED_IMAGE_URLS_KEY(propertyIdStr);
          const stored = sessionStorage.getItem(key);
          const deletedUrls: string[] = stored ? JSON.parse(stored) : [];
          if (deletedUrls.length > 0) {
            const deletedSet = new Set(deletedUrls);
            const filteredImageUrlList = filterOutDeletedUrls(
              data.result.imageUrlList,
              deletedUrls,
            );
            // If current thumbnail was deleted, clear it or use first remaining image
            let thumbnail = data.result.thumbnail;
            if (thumbnail && typeof thumbnail === 'object') {
              const thumbUrls = Object.values(thumbnail) as string[];
              const thumbUrl = thumbUrls[0];
              if (typeof thumbUrl === 'string' && deletedSet.has(thumbUrl)) {
                const firstCategory = Object.keys(filteredImageUrlList)[0];
                const firstUrl = firstCategory
                  ? filteredImageUrlList[firstCategory]?.[0]
                  : undefined;
                thumbnail = firstUrl
                  ? { [firstCategory || 'default']: firstUrl }
                  : undefined;
              }
            }
            data.result = {
              ...data.result,
              imageUrlList: filteredImageUrlList,
              thumbnail: thumbnail as Record<string, string> | undefined,
            };
          }
        } catch (_) {
          // ignore
        }
      }
      // Old listings can return duplicated URLs after image updates;
      // normalize response so edit/thumbnail/confirmation screens remain stable.
      if (data?.result?.imageUrlList) {
        data.result = {
          ...data.result,
          imageUrlList: dedupeImageUrlList(data.result.imageUrlList),
        };
      }
      return data;
    } catch (error) {
      // Handle error
      console.error('Error fetching data:', error);
      throw error;
    }
  },

  getBookingSettingsByPropertyId: async (propertyId) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .get(
          `${API_BASE_URL}/getBookingSettingsByPropertyId?propertyId=${propertyId}&_t=${Date.now()}`,
          { headers },
        )
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error fetching data:', error);
      throw error;
    }
  },

  // Function to post data to the API
  saveProperty: async (data: any): Promise<ApiResponse> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .post(`${API_BASE_URL}/saveProperty`, data, {
          headers,
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error posting data:', error);
      throw error;
    }
  },

  /** Create listing(s) from Airbnb URLs (host dashboard assisted flow). */
  bulkOnboardFromAirbnb: async (data: {
    urls: string[];
    hostId: number;
    businessType?: string;
  }): Promise<ApiResponse> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .post(`${API_BASE_URL}/bulkOnboardFromAirbnb`, data, {
          headers,
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          // Re-throw so callers can treat 504 / network / abort like real failures (poll, retry UX).
          // Returning null produced `{ message: 'No data' }` and hid recoverable gateway timeouts.
          throw error;
        });
      return response?.data || { success: false, message: 'No data', result: [] };
    } catch (error) {
      console.error('Error bulk onboarding from Airbnb:', error);
      throw error;
    }
  },

  submitAssistedListing: async (data: {
    url: string;
    hostId: number;
    businessType?: string;
  }): Promise<ApiResponse> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .post(`${API_BASE_URL}/submitAssistedListing`, data, { headers })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          throw error;
        });
      return response?.data || { success: false, message: 'No data' };
    } catch (error) {
      console.error('Error submitting assisted listing:', error);
      throw error;
    }
  },

  /** Starts an Airbnb import in the background; poll getAirbnbOnboardingStatus with the returned jobId. */
  startAirbnbOnboarding: async (data: {
    url: string;
    businessType?: string;
  }): Promise<any> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
    };
    const response = await axios.post(`${API_BASE_URL}/startAirbnbOnboarding`, data, { headers });
    return response.data;
  },

  /** Logged-out import: no Authorization header; the returned guestToken is the credential. */
  startGuestAirbnbOnboarding: async (url: string): Promise<any> => {
    const response = await axios.post(`${API_BASE_URL}/startGuestAirbnbOnboarding`, { url });
    return response.data;
  },

  getGuestAirbnbOnboardingStatus: async (jobId: string, token: string): Promise<any> => {
    const response = await axios.get(`${API_BASE_URL}/guestAirbnbOnboardingStatus`, {
      params: { jobId, token },
    });
    return response.data;
  },

  /** After login: makes the guest import the logged-in host's listing. */
  claimAirbnbOnboarding: async (jobId: string, guestToken: string): Promise<any> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
    };
    const response = await axios.post(
      `${API_BASE_URL}/claimAirbnbOnboarding`,
      { jobId, guestToken },
      { headers },
    );
    return response.data;
  },

  /** Dashboard notices about the host's link imports that did not end with a listing under approval. */
  getImportIssues: async (): Promise<any[]> => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` };
    const response = await axios.get(`${API_BASE_URL}/importIssues`, { headers });
    return Array.isArray(response.data) ? response.data : [];
  },

  dismissImportIssue: async (jobId: string): Promise<void> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
    };
    await axios.post(`${API_BASE_URL}/dismissImportIssue`, { jobId }, { headers });
  },

  getAirbnbOnboardingStatus: async (jobId: string): Promise<any> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
    };
    const response = await axios.get(`${API_BASE_URL}/airbnbOnboardingStatus`, {
      headers,
      params: { jobId },
    });
    return response.data;
  },

  updateProperty: async (data: any): Promise<ApiResponse> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .post(`${API_BASE_URL}/updateProperty`, data, {
          headers,
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error update data:', error);
      throw error;
    }
  },

  updateLiveWalkthroughSetting: async (propertyId: string | number, allowLiveWalkthrough: boolean): Promise<any> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .patch(`${API_BASE_URL}/${propertyId}/liveWalkthrough`, { allowLiveWalkthrough }, { headers })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return response?.data || null;
    } catch (error) {
      console.error('Error updating live walkthrough setting:', error);
      throw error;
    }
  },

  completePropertyOnboarding: async (data) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .post(`${API_BASE_URL}/completePropertyOnboarding`, data, { headers })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
            return null;
          }
          // For 400 and other non-403 errors, return the error response data
          // so callers can inspect responseCode / responseMessage.
          if (error?.response?.data) {
            return { data: error.response.data, _httpStatus: error.response.status };
          }
          return null;
        });
      return (
        (response as any)?._httpStatus
          ? { ...(response as any).data, _httpStatus: (response as any)._httpStatus }
          : response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error posting data:', error);
      throw error;
    }
  },

  uploadPhotos: async (images, thumbnail, propertyId) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Access-Control-Allow-Origin': '*',
    };
    const formData = new FormData();

    Object.keys(images).forEach((key) => {
      const keyImagesList = images[key];

      keyImagesList.forEach((image) => {
        formData.append(`images[${key}]`, image);
      });
    });

    if (!isEmpty(thumbnail)) {
      formData.append(
        `thumbnail[${Object.keys(thumbnail)[0]}]`,
        Object.values(thumbnail)[0] as string,
      );
    }
    formData.append('propertyId', propertyId);
    formData.append('onboardingStage', '2');

    try {
      const response = await axios
        .post(`${API_BASE_URL}/images/upload`, formData, { headers })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error uploading images:', error);
      throw error;
    }
  },

  updatePhotos: async (
    images: any,
    updatedImages: any,
    thumbnail: any,
    thumbnailUrl: any,
    deletedImages: string[] = [],
    propertyId: string,
  ): Promise<ApiResponse> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Access-Control-Allow-Origin': '*',
    };
    const formData = new FormData();
    Object.keys(images).forEach((key) => {
      const keyImagesList = images[key];

      keyImagesList.forEach((image) => {
        formData.append(`addImages[${key}]`, image);
      });
    });

    Object.keys(updatedImages).forEach((key) => {
      const keyImagesList = updatedImages[key];

      keyImagesList.forEach((image) => {
        formData.append(`existingImagesCategoryUpdates[${key}]`, image);
      });
    });
    if (!isEmpty(thumbnail)) {
      formData.append(
        `thumbnail[${Object.keys(thumbnail)[0]}]`,
        Object.values(thumbnail)[0] as string,
      );
    }
    if (!isEmpty(thumbnailUrl)) {
      formData.append(
        `thumbnailUrl[${Object.keys(thumbnailUrl)[0]}]`,
        Object.values(thumbnailUrl)[0] as string,
      );
    }
    const effectivePropertyId =
      String(propertyId || localStorage.getItem('id') || '');
    if (deletedImages.length > 0) {
      deletedImages.forEach((url) => formData.append('deleteImagesURL', url));
      setDeletedImageUrlsForProperty(effectivePropertyId, deletedImages);
    }
    formData.append('propertyId', effectivePropertyId);

    try {
      const response = await axios
        .post(`${API_BASE_URL}/images/update`, formData, { headers })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error uploading images:', error);
      throw error;
    }
  },

  getProperty: async (
    postId: string | number = 1,
    pageNumber = 1,
    countPerPage = 100,
    search = '',
  ) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const searchParam = search
        ? `&search=${encodeURIComponent(search)}`
        : '';
      const response = await axios
        .get(
          `${API_BASE_URL}/getAllPropertiesForHost?hostId=${postId}&pageNumber=${pageNumber}&countPerPage=${countPerPage}${searchParam}`,

          {
            headers,
          },
        )
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error wile fetching the post:', error);
      throw error;
    }
  },

  getAllProperties: async (pages = 1) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .get(
          `${ADMIN_BASE_URL}/getAllProperties?pageNumber=${pages}&countPerPage=20`,

          {
            headers,
          },
        )
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error wile fetching the post:', error);
      throw error;
    }
  },

  deactivateProperty: async (propertyId: number | string): Promise<ApiResponse> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .post(`${ADMIN_BASE_URL}/property/deactivate/${propertyId}`, {}, { headers })
        .catch((error) => {
          handleApiError(error);
          throw error;
        });
      return (
        response?.data || { success: false, message: 'Deactivation failed', result: null }
      );
    } catch (error) {
      console.error('Error deactivating property:', error);
      throw error;
    }
  },

  updateBusinessType: async (
    propertyId: number | string,
    businessType: 'B2B' | 'B2C',
  ): Promise<ApiResponse<any>> => {
    const headers = getStandardHeaders();
    try {
      const response = await axios
        .put(`${ADMIN_BASE_URL}/property/${propertyId}/businessType`, { businessType }, { headers })
        .catch((error) => {
          handleApiError(error);
          throw error;
        });
      return response?.data || { success: false, message: 'Update failed', result: null };
    } catch (error) {
      console.error('Error updating business type:', error);
      throw error;
    }
  },

  /**
   * Admin-only: records whether the garden of a garden-unclear listing is private, shared or there is none.
   * The listing can't be approved until this is set (see PendingApproval).
   */
  updateGardenType: async (
    propertyId: number | string,
    gardenType: 'PRIVATE' | 'SHARED' | 'NONE',
  ): Promise<ApiResponse<any>> => {
    const headers = getStandardHeaders();
    try {
      const response = await axios
        .put(`${ADMIN_BASE_URL}/property/${propertyId}/gardenType`, { gardenType }, { headers })
        .catch((error) => {
          handleApiError(error);
          throw error;
        });
      return response?.data || { success: false, message: 'Update failed', result: null };
    } catch (error) {
      console.error('Error updating garden type:', error);
      throw error;
    }
  },

  /**
   * Admin-only: records whether the pool of a pool-unclear listing is private or shared.
   * The listing can't be approved until this is set (see PendingApproval).
   */
  updatePoolType: async (
    propertyId: number | string,
    poolType: 'PRIVATE' | 'SHARED',
  ): Promise<ApiResponse<any>> => {
    const headers = getStandardHeaders();
    try {
      const response = await axios
        .put(`${ADMIN_BASE_URL}/property/${propertyId}/poolType`, { poolType }, { headers })
        .catch((error) => {
          handleApiError(error);
          throw error;
        });
      return response?.data || { success: false, message: 'Update failed', result: null };
    } catch (error) {
      console.error('Error updating pool type:', error);
      throw error;
    }
  },

  getPendingApprovalProperties: async (filters?: {
    fromDate?: string;
    toDate?: string;
    hostName?: string;
    propertyId?: string;
    status?: string;
    excludeAdmins?: boolean;
    hostType?: string;
    businessType?: 'B2B' | 'B2C';
  }): Promise<ApiResponse<any[]>> => {
    const headers = getStandardHeaders();
    try {
      const params: Record<string, string> = {};
      if (filters?.fromDate) params.fromDate = filters.fromDate;
      if (filters?.toDate) params.toDate = filters.toDate;
      if (filters?.hostName) params.hostName = filters.hostName;
      if (filters?.propertyId) params.propertyId = filters.propertyId;
      if (filters?.status) params.status = filters.status;
      if (filters?.excludeAdmins != null) params.excludeAdmins = String(filters.excludeAdmins);
      if (filters?.hostType) params.hostType = filters.hostType;
      if (filters?.businessType) params.businessType = filters.businessType;
      const response = await axios
        .get(`${ADMIN_BASE_URL}/property/pendingApproval`, { headers, params })
        .catch((error) => {
          handleApiError(error);
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      console.error('Error fetching pending approval properties:', error);
      throw error;
    }
  },

  getAllPropertiesAdmin: async (filters?: {
    businessType?: 'B2B' | 'B2C';
    pageNumber?: number;
    countPerPage?: number;
    search?: string;
  }): Promise<ApiResponse<any[]>> => {
    const headers = getStandardHeaders();
    try {
      const params: Record<string, string> = {
        pageNumber: String(filters?.pageNumber ?? 1),
        countPerPage: String(filters?.countPerPage ?? 50),
      };
      if (filters?.businessType) params.businessType = filters.businessType;
      if (filters?.search?.trim()) params.search = filters.search.trim();
      const response = await axios
        .get(`${ADMIN_BASE_URL}/getAllProperties`, { headers, params })
        .catch((error) => {
          handleApiError(error);
          return null;
        });
      return response?.data || { success: false, message: 'No data', result: [] };
    } catch (error) {
      console.error('Error fetching all properties:', error);
      throw error;
    }
  },

  activateProperty: async (
    propertyId: number | string,
  ): Promise<ApiResponse<any>> => {
    const headers = getStandardHeaders();
    try {
      const response = await axios
        .post(`${ADMIN_BASE_URL}/property/activate/${propertyId}`, {}, { headers })
        .catch((error) => {
          handleApiError(error);
          throw error;
        });
      return (
        response?.data || { success: false, message: 'Activation failed', result: null }
      );
    } catch (error) {
      console.error('Error activating property:', error);
      throw error;
    }
  },

  /**
   * Approves a batch of pending property listings in one call. Backend reuses the same
   * per-property validation/email/ES-sync logic as `activateProperty`, so a property that's no
   * longer pending approval is reported back as an individual failure instead of failing the
   * batch.
   */
  activatePropertiesBulk: async (
    propertyIds: Array<number | string>,
  ): Promise<ApiResponse<any>> => {
    const headers = getStandardHeaders();
    try {
      const response = await axios
        .post(`${ADMIN_BASE_URL}/property/activate/bulk`, { propertyIds }, { headers })
        .catch((error) => {
          handleApiError(error);
          throw error;
        });
      return (
        response?.data || { success: false, message: 'Bulk activation failed', result: null }
      );
    } catch (error) {
      console.error('Error bulk activating properties:', error);
      throw error;
    }
  },

  /**
   * Admin-only: stops a property from being overwritten by Airbnb pricing going forward
   * (recurring cron re-scrape), without clearing the stored bnbUrl or touching already-synced
   * calendar prices. The host's own price takes over the next time they save pricing.
   */
  unsyncAirbnbPricing: async (
    propertyId: number | string,
  ): Promise<ApiResponse<any>> => {
    const headers = getStandardHeaders();
    try {
      const response = await axios
        .post(`${ADMIN_BASE_URL}/property/unsyncAirbnb/${propertyId}`, {}, { headers })
        .catch((error) => {
          handleApiError(error);
          throw error;
        });
      return (
        response?.data || { success: false, message: 'Unsync failed', result: null }
      );
    } catch (error) {
      console.error('Error unsyncing Airbnb pricing for property:', error);
      throw error;
    }
  },

  /**
   * Registers a property with PriceLabs (POST /listings) and seeds its initial calendar.
   * Callable by the host who owns propertyId, or by an admin for any property — backend
   * enforces this, not just the frontend.
   */
  onboardPriceLabs: async (
    propertyId: number | string,
  ): Promise<ApiResponse<any>> => {
    const headers = getStandardHeaders();
    try {
      const response = await axios
        .post(`${API_BASE_URI}/pricelabs/admin/onboard/${propertyId}`, {}, { headers })
        .catch((error) => {
          handleApiError(error);
          throw error;
        });
      return (
        response?.data || { success: false, message: 'PriceLabs onboarding failed', result: null }
      );
    } catch (error) {
      console.error('Error onboarding property to PriceLabs:', error);
      throw error;
    }
  },

  /**
   * Checks whether a property is onboarded to PriceLabs and, if so, PriceLabs's own
   * "Enable Price Sync" toggle state — that toggle has no API to flip it on, only to read it
   * (see docs/PRICELABS_INTEGRATION.md), so the UI can only surface it, not change it.
   */
  getPriceLabsStatus: async (
    propertyId: number | string,
  ): Promise<ApiResponse<any>> => {
    const headers = getStandardHeaders();
    try {
      const response = await axios
        .get(`${API_BASE_URI}/pricelabs/admin/status/${propertyId}`, { headers })
        .catch((error) => {
          handleApiError(error);
          throw error;
        });
      return (
        response?.data || { success: false, message: 'PriceLabs status check failed', result: null }
      );
    } catch (error) {
      console.error('Error checking PriceLabs status for property:', error);
      throw error;
    }
  },

  /**
   * Rejects a pending property listing. Backend is expected to send the rejection email to
   * the host (from the existing pending/nudge email table, not the property master table)
   * and should NOT change the property's own approval-status persistence semantics beyond
   * marking it rejected.
   */
  rejectProperty: async (
    propertyId: number | string,
  ): Promise<ApiResponse<any>> => {
    const headers = getStandardHeaders();
    try {
      const response = await axios
        .post(`${ADMIN_BASE_URL}/property/reject/${propertyId}`, {}, { headers })
        .catch((error) => {
          handleApiError(error);
          throw error;
        });
      return (
        response?.data || { success: false, message: 'Rejection failed', result: null }
      );
    } catch (error) {
      console.error('Error rejecting property:', error);
      throw error;
    }
  },

  /**
   * Rejects a batch of pending property listings in one call. Backend reuses the same
   * per-property validation/email logic as `rejectProperty`, so a property that's no longer
   * pending approval is reported back as an individual failure instead of failing the batch.
   */
  rejectPropertiesBulk: async (
    propertyIds: Array<number | string>,
  ): Promise<ApiResponse<any>> => {
    const headers = getStandardHeaders();
    try {
      const response = await axios
        .post(`${ADMIN_BASE_URL}/property/reject/bulk`, { propertyIds }, { headers })
        .catch((error) => {
          handleApiError(error);
          throw error;
        });
      return (
        response?.data || { success: false, message: 'Bulk rejection failed', result: null }
      );
    } catch (error) {
      console.error('Error bulk rejecting properties:', error);
      throw error;
    }
  },

  scrapePriceForProperty: async (propertyId: number | string) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .get(`${API_BASE_URL}/scrapePriceForProperty?propertyId=${propertyId}`, { headers })
        .catch((error) => {
          handleApiError(error);
          throw error;
        });
      return (
        response?.data || { success: false, message: 'Price scrape failed', result: null }
      );
    } catch (error) {
      console.error('Error scraping price for property:', error);
      throw error;
    }
  },

  scrapeAvailabilityForProperty: async (propertyId: number | string) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .get(`${API_BASE_URL}/scrapeAvailabilityForProperty?propertyId=${propertyId}`, { headers })
        .catch((error) => {
          handleApiError(error);
          throw error;
        });
      return (
        response?.data || { success: false, message: 'Availability scrape failed', result: null }
      );
    } catch (error) {
      console.error('Error scraping availability for property:', error);
      throw error;
    }
  },

  getAllHost: async (pages = 1) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .get(
          `${ADMIN_BASE_URL}/getAllHosts?pageNumber=${pages}&countPerPage=20`,

          {
            headers,
          },
        )
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error wile fetching the post:', error);
      throw error;
    }
  },

  getPropertyCategoriesList: async () => {
    try {
      const response = await axios
        .get(`${API_BASE_URL}/getPropertyCategoryList`)
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error wile fetching the post:', error);
      throw error;
    }
  },

  saveCategories: async (data) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .post(`${API_BASE_URL}/savePropertyCategories`, data, { headers })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error posting data:', error);
      throw error;
    }
  },

  getFullPropertyDetailsById: async (data) => {
    const startDate =
      data?.checkIn || `${moment().add(3, 'days').format('YYYY-MM-DD')}`;
    const endDate =
      data?.checkOut || `${moment().add(7, 'days').format('YYYY-MM-DD')}`;
    const body = {
      propertyId: data?.propertyId,
      checkIn: startDate,
      checkOut: endDate,
    };

    if ((data as any)?.adults)
      (body as any).adults = (data as any)?.adults || 2;
    if ((data as any)?.pets) (body as any).pets = (data as any)?.pets;
    if ((data as any)?.children)
      (body as any).children = (data as any)?.children;
    if ((data as any)?.infants) (body as any).infants = (data as any)?.infants;

    try {
      const response = await axios
        .post(`${API_BASE_URL}/propertyDetails`, body)
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
          return null;
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error posting data:', error);
      throw error;
    }
  },

  /**
   * Writes host calendar nightly prices (property_price_calendar). Body is a JSON array
   * as required by POST /property/syncCalendarPricing.
   */
  syncCalendarPricing: async (
    payload: Array<{
      propertyId: number;
      prices: Array<{ date: string; pricePerNight: number }>;
      // true = only change the dates sent; false/omitted = replace the whole calendar
      mergeMode?: boolean;
      source?: 'HOST' | 'AIRBNB' | 'ELIVAAS' | 'PRICELABS';
    }>,
  ) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .post(`${API_BASE_URL}/syncCalendarPricing`, payload, { headers })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
            return null;
          }
          if (error?.response?.data) {
            return {
              data: error.response.data,
              _httpStatus: error.response.status,
            };
          }
          return null;
        });
      return (response as any)?._httpStatus
        ? {
            ...(response as any).data,
            _httpStatus: (response as any)._httpStatus,
          }
        : response?.data || { success: false, message: 'No data' };
    } catch (error) {
      console.error('Error syncCalendarPricing:', error);
      throw error;
    }
  },

  getAllReviewsForProperty: async (
    id: number | string,
  ): Promise<ApiResponse<PropertyReview[]>> => {
    try {
      // const headers = {
      //   Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      //   'Content-Type': 'application/json',
      //   'Access-Control-Allow-Origin': '*',
      // };
      const response = await axios
        .get(`${API_BASE_URL}/getAllReviewsForProperty?propertyId=${id}`)
        .catch((error) => {
          if (error?.response?.status === 403) {
            console.log('error: ', error);
            // localStorage.clear();
            // sessionStorage.clear();
            // window.open('/sign-in', '_self');
          }
        });
      return (
        response?.data || { success: false, message: 'No data', result: [] }
      );
    } catch (error) {
      // Handle error
      console.error('Error wile fetching the post:', error);
      throw error;
    }
  },

  // Add more functions for other API calls as needed
};

export default PropertyService;
